import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Button, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { LOOSE_ENDS_KEY } from './loose-ends';
import './loose-ends.css';

// Guards against loose ends (2 November 2026). SJM deleted the flows its improved copies still opened, and menu buttons
// ended the chat in silence. Before a flow is deleted or switched off, the flows whose Run another flow steps open it
// are named, and a delete can point those steps at another flow first.

const MUTED = 'var(--rr-color-text-muted)';

/** The steps in switched-on flows (other than the flow itself) that open it. */
const liveUses = (uses: readonly flows.FlowUse[] | undefined, id: string) => (uses ?? []).filter((u) => u.is_active && u.flow_id !== id);

/** One line per step: the flow and the step's name. */
function UsesList({ uses, t }: { uses: readonly flows.FlowUse[]; t: Translate }) {
  return (
    <ul className="rr-loose-ends__uses" data-testid="flow-used-by">
      {uses.map((u) => (
        <li key={`${u.flow_id}:${u.step_id}`}>
          <Text role="bodySmall">
            {u.step_name?.trim()
              ? t('flows.delete.usedLine', { flow: u.flow_name, step: u.step_name.trim() })
              : t('flows.delete.usedLineUnnamed', { flow: u.flow_name })}
          </Text>
        </li>
      ))}
    </ul>
  );
}

/**
 * Switching a flow off: when switched-on flows open it, a confirmation names them first, because customers who reach
 * those steps will be told it is not available. Otherwise it switches off as before. The check is a courtesy: when it
 * cannot be made, the flow switches off without it, as it always did.
 */
export async function confirmSwitchOff(flow: { id: string; name: string }, t: Translate, switchOff: () => void): Promise<void> {
  let uses: flows.FlowUse[] = [];
  try {
    uses = liveUses((await flows.flowUsedBy(flow.id)).data, flow.id);
  } catch {
    uses = [];
  }
  if (uses.length === 0) { switchOff(); return; }
  openConfirmModal({
    title: t('flows.switchOff.title', { name: flow.name }),
    children: (
      <Stack gap={8} data-testid="flow-switch-off">
        <Text role="bodySmall">{t('flows.switchOff.body', { name: flow.name })}</Text>
        <UsesList uses={uses} t={t} />
        <Text role="caption" c={MUTED}>{t('flows.switchOff.hint')}</Text>
      </Stack>
    ),
    labels: { confirm: t('flows.switchOff.confirm'), cancel: t('action.cancel') },
    confirmProps: { color: 'red' },
    onConfirm: switchOff
  });
}

/**
 * Deleting a flow: its run history goes with it. When switched-on flows open it, the dialog names them and offers
 * "Point those steps at" one of the business's other flows (Delete), or Delete anyway, which leaves them leading
 * nowhere. A plain delete the service refuses with FLOW_IN_USE (a flow switched on meanwhile) shows the same list.
 */
export function DeleteFlowDialog({ flow, onClose, onDeleted }: {
  /** The flow to delete; null keeps the dialog closed. */
  flow: { id: string; name: string } | null;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const id = flow?.id ?? '';
  const [standIn, setStandIn] = useState('');
  const [refused, setRefused] = useState<flows.FlowUse[] | null>(null);
  useEffect(() => { setStandIn(''); setRefused(null); }, [id]);

  const usedBy = useQuery({
    queryKey: ['flows', 'used-by', id],
    queryFn: () => flows.flowUsedBy(id),
    select: (r) => r.data,
    enabled: !!flow,
    staleTime: 0,
    retry: false
  });
  const list = useQuery({ queryKey: ['flows'], queryFn: () => flows.listFlows(), staleTime: 30_000, enabled: !!flow });
  const uses = liveUses(refused ?? usedBy.data, id);
  const others = useMemo(() => (list.data?.data ?? [])
    .filter((f) => f.id !== id)
    .sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name)), [list.data, id]);

  const del = useMutation({
    mutationFn: (opts: { replaceWith?: string; force?: boolean }) => flows.deleteFlow(id, opts),
    onSuccess: (_r, opts) => {
      const target = others.find((f) => f.id === opts.replaceWith);
      // The flow is gone: its own queries are dropped rather than asked again (a 404 each).
      qc.removeQueries({ queryKey: ['flow', id] });
      qc.removeQueries({ queryKey: ['flows', 'used-by', id] });
      void qc.invalidateQueries({ queryKey: ['flows'], exact: true });
      void qc.invalidateQueries({ queryKey: LOOSE_ENDS_KEY });
      // Flows whose steps were pointed elsewhere.
      if (opts.replaceWith) void qc.invalidateQueries({ queryKey: ['flow'] });
      notifySuccess(target ? t('flows.delete.relinked', { name: flow!.name, target: target.name }) : t('flows.toast.deleted', { name: flow!.name }), t('toast.deleted'));
      onClose();
      onDeleted?.();
    },
    onError: (e) => {
      if (flows.isFlowInUse(e)) { setRefused(e.body.error.used_by); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('flows.toast.deleteFailed'));
    }
  });

  const checking = !!flow && usedBy.isPending && !refused;
  const inUse = uses.length > 0;
  return (
    <Modal opened={!!flow} onClose={onClose} title={flow ? t('flows.modal.delete.confirmNamed', { name: flow.name }) : ''} size="md">
      <Stack gap="sm" data-testid="flow-delete">
        {checking ? (
          <Stack gap={4}>
            <Skeleton height={36} />
            <Text role="caption" c={MUTED}>{t('flows.delete.checking')}</Text>
          </Stack>
        ) : inUse && (
          <>
            <Alert color="warning" title={t('flows.delete.usedTitle')} data-testid="flow-delete-used">
              <Stack gap={6}>
                <UsesList uses={uses} t={t} />
                <Text role="caption">{t('flows.delete.usedHint')}</Text>
              </Stack>
            </Alert>
            <Select
              label={t('flows.delete.pointAt')}
              description={t('flows.delete.pointAtHint')}
              value={standIn}
              onChange={(e) => setStandIn(e.currentTarget.value)}
              data={[
                { value: '', label: t('flows.delete.pointAtChoose') },
                ...others.map((f) => ({ value: f.id, label: f.is_active ? f.name : `${f.name} (${t('status.inactive')})` }))
              ]}
              data-testid="flow-delete-stand-in"
            />
          </>
        )}
        <Text role="bodySmall">{t('flows.delete.history')}</Text>
        <Group justify="flex-end" gap="sm" wrap="wrap">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          {inUse && (
            <Button variant="default" color="red" onClick={() => del.mutate({ force: true })} loading={del.isPending && del.variables?.force === true}
              disabled={del.isPending} data-testid="flow-delete-anyway">
              {t('flows.delete.anyway')}
            </Button>
          )}
          <Button color="red" disabled={checking || (inUse && !standIn) || del.isPending}
            loading={del.isPending && del.variables?.force !== true}
            onClick={() => del.mutate(inUse ? { replaceWith: standIn } : {})} data-testid="flow-delete-confirm">
            {t('action.delete')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
